import {getAuth} from 'firebase/auth';
import {getFunctions, httpsCallable} from 'firebase/functions';
import {addDoc, collection, doc, getDoc, onSnapshot, query, updateDoc, where} from 'firebase/firestore';
import {db} from '../../../shared/services/firebase';

const FUNCTIONS_REGION = 'europe-west1';

const FIREBASE_TO_DOMAIN_CODE = {
  'permission-denied': 'TENANT_PERMISSION_DENIED',
  unauthenticated: 'TENANT_UNAUTHENTICATED',
  'not-found': 'TENANT_NOT_FOUND',
  'invalid-argument': 'TENANT_INVALID_ARGUMENT',
  'failed-precondition': 'TENANT_FAILED_PRECONDITION'
};

const DOMAIN_ERROR_MESSAGES = {
  TENANT_PERMISSION_DENIED: 'You do not have permission to perform this action.',
  TENANT_UNAUTHENTICATED: 'Please sign in again and retry.',
  TENANT_NOT_FOUND: 'Tenant not found.',
  TENANT_INVALID_ARGUMENT: 'Invalid request. Please review the provided values.',
  TENANT_FAILED_PRECONDITION: 'This action cannot be completed right now.',
  TENANT_SUBSCRIBE_FAILED: 'Failed to fetch tenant details.',
  SUBTENANTS_SUBSCRIBE_FAILED: 'Failed to fetch sub-tenants.',
  CREATE_SUBTENANT_FAILED: 'Failed to create sub-tenant.',
  ADD_MEMBER_FAILED: 'Failed to add member.',
  ADD_MEMBER_ALREADY_EXISTS: 'The user already belongs to a tenant.',
  USER_NOT_FOUND: 'User not found.',
  REMOVE_MEMBER_FAILED: 'Failed to remove member.',
  TRANSFER_EDGE_FAILED: 'Failed to transfer edge.',
  CREATE_EDGE_FAILED: 'Failed to create edge.',
  CREATE_EDGE_ALREADY_EXISTS: 'An edge with this ID already exists.',
  INVALID_EDGE_INPUT: 'All required fields (ID, address) must be provided.',
  COMPLETE_EDGE_MIGRATION_FAILED: 'Failed to complete edge migration.',
  LOAD_TENANT_CONTEXT_FAILED: 'Failed to load tenant context.',
  SET_TENANT_CLAIM_FAILED: 'Failed to set current tenant claims.',
  INVALID_SUBTENANT_NAME: 'Sub-tenant name is required.',
  MISSING_OWNER_IDENTITY: 'Unable to resolve owner identity.',
  MISSING_PARENT_TENANT: 'Missing parent tenant hierarchy for new sub-tenant.',
  INVALID_TRANSFER_INPUT: 'Target tenant and edge are required for transfer.'
};

const toTenant = (snapshot) => ({id: snapshot.id, ...snapshot.data()});

const buildSubTenantsPath = (tenantPath) => (
  tenantPath.endsWith('/') ? `${tenantPath}tenants` : `${tenantPath}/tenants`
);

const getFirebaseCode = (error) => {
  const code = error?.code;
  if (!code || typeof code !== 'string') return null;
  if (code.includes('/')) return code.split('/')[1];
  if (code.includes(':')) return code.split(':')[1];
  return code;
};

const toTenantError = (error, fallbackCode, fallbackMessage) => {
  const firebaseCode = getFirebaseCode(error);
  const code = FIREBASE_TO_DOMAIN_CODE[firebaseCode] || fallbackCode;
  const message = DOMAIN_ERROR_MESSAGES[code] || error?.message || fallbackMessage;
  return {
    code,
    message,
    rawCode: error?.code || null,
    firebaseCode,
    cause: error || null
  };
};

const logTenantError = (context, errorInfo) => {
  console.error(`[tenantsApi:${context}] ${errorInfo.code}: ${errorInfo.message}`, errorInfo.cause || errorInfo);
};

const ok = (data = null) => ({ok: true, data, error: null});
const fail = (error) => ({ok: false, data: null, error});

const notifyError = (handler, errorInfo) => {
  if (typeof handler === 'function') {
    handler(errorInfo);
  }
};

const callTenantFunction = (name) => {
  const functions = getFunctions(undefined, FUNCTIONS_REGION);
  return httpsCallable(functions, name);
};

const subscribeTenant = (path, {onNext, onError}) => onSnapshot(
  doc(db, path),
  (docSnap) => {
    if (!docSnap.exists()) {
      const errorInfo = {
        code: 'TENANT_NOT_FOUND',
        message: DOMAIN_ERROR_MESSAGES.TENANT_NOT_FOUND,
        rawCode: null,
        firebaseCode: null,
        cause: null
      };
      logTenantError('subscribeTenant', errorInfo);
      notifyError(onError, errorInfo);
      return;
    }

    onNext(toTenant(docSnap));
  },
  (error) => {
    const errorInfo = toTenantError(error, 'TENANT_SUBSCRIBE_FAILED', 'Failed to fetch tenant details');
    logTenantError('subscribeTenant', errorInfo);
    notifyError(onError, errorInfo);
  }
);

const subscribeSubTenants = (path, currentTenantId, {onNext, onError}) => {
  const subTenantsRef = collection(db, buildSubTenantsPath(path));
  const subTenantsQuery = currentTenantId
    ? query(subTenantsRef, where('parentTenantIds', 'array-contains', currentTenantId))
    : subTenantsRef;

  return onSnapshot(
    subTenantsQuery,
    (snapshot) => {
      const subTenants = [];
      snapshot.forEach((subTenantDoc) => {
        subTenants.push(toTenant(subTenantDoc));
      });
      onNext(subTenants);
    },
    (error) => {
      const errorInfo = toTenantError(error, 'SUBTENANTS_SUBSCRIBE_FAILED', 'Failed to fetch sub-tenants');
      logTenantError('subscribeSubTenants', errorInfo);
      notifyError(onError, errorInfo);
    }
  );
};

const createSubTenant = async ({path, name, user, tenant}) => {
  if (!name) {
    const errorInfo = {
      code: 'INVALID_SUBTENANT_NAME',
      message: DOMAIN_ERROR_MESSAGES.INVALID_SUBTENANT_NAME,
      rawCode: null,
      firebaseCode: null,
      cause: null
    };
    logTenantError('createSubTenant', errorInfo);
    return fail(errorInfo);
  }

  const ownerEmail = user?.email || user?.uid;
  if (!ownerEmail) {
    const errorInfo = {
      code: 'MISSING_OWNER_IDENTITY',
      message: DOMAIN_ERROR_MESSAGES.MISSING_OWNER_IDENTITY,
      rawCode: null,
      firebaseCode: null,
      cause: null
    };
    logTenantError('createSubTenant', errorInfo);
    return fail(errorInfo);
  }

  const currentParentIds = tenant?.parentTenantIds || [tenant?.id].filter(Boolean);
  if (!currentParentIds.length) {
    const errorInfo = {
      code: 'MISSING_PARENT_TENANT',
      message: DOMAIN_ERROR_MESSAGES.MISSING_PARENT_TENANT,
      rawCode: null,
      firebaseCode: null,
      cause: null
    };
    logTenantError('createSubTenant', errorInfo);
    return fail(errorInfo);
  }

  try {
    const newDocRef = await addDoc(collection(db, buildSubTenantsPath(path)), {
      name,
      owners: [ownerEmail],
      admins: [],
      viewers: [],
      edges: [],
      parentTenantIds: currentParentIds
    });

    await updateDoc(newDocRef, {
      parentTenantIds: [...currentParentIds, newDocRef.id]
    });

    return ok({id: newDocRef.id, path: newDocRef.path});
  } catch (error) {
    const errorInfo = toTenantError(error, 'CREATE_SUBTENANT_FAILED', 'Failed to create sub-tenant');
    logTenantError('createSubTenant', errorInfo);
    return fail(errorInfo);
  }
};

const addTenantMember = async ({tenantPath, email, role}) => {
  try {
    const addMemberFn = callTenantFunction('addTenantMember');
    await addMemberFn({tenantId: tenantPath, email, role});
    return ok();
  } catch (error) {
    const firebaseCode = getFirebaseCode(error);
    if (firebaseCode === 'not-found' && error?.message?.includes('No registered user')) {
      const errorInfo = {code: 'USER_NOT_FOUND', message: DOMAIN_ERROR_MESSAGES.USER_NOT_FOUND, rawCode: error.code, firebaseCode, cause: error};
      logTenantError('addTenantMember', errorInfo);
      return fail(errorInfo);
    }
    if (firebaseCode === 'already-exists') {
      const errorInfo = {code: 'ADD_MEMBER_ALREADY_EXISTS', message: DOMAIN_ERROR_MESSAGES.ADD_MEMBER_ALREADY_EXISTS, rawCode: error.code, firebaseCode, cause: error};
      logTenantError('addTenantMember', errorInfo);
      return fail(errorInfo);
    }
    const errorInfo = toTenantError(error, 'ADD_MEMBER_FAILED', 'Failed to add member');
    logTenantError('addTenantMember', errorInfo);
    return fail(errorInfo);
  }
};

const removeTenantMember = async ({tenantPath, email, role}) => {
  try {
    const removeMemberFn = callTenantFunction('removeTenantMember');
    await removeMemberFn({tenantId: tenantPath, email, role});
    return ok();
  } catch (error) {
    const errorInfo = toTenantError(error, 'REMOVE_MEMBER_FAILED', 'Failed to remove member');
    logTenantError('removeTenantMember', errorInfo);
    return fail(errorInfo);
  }
};

const createEdge = async ({id, name, address, geolocation, balenaUuid}) => {
  if (!id || !address) {
    const errorInfo = {
      code: 'INVALID_EDGE_INPUT',
      message: DOMAIN_ERROR_MESSAGES.INVALID_EDGE_INPUT,
      rawCode: null,
      firebaseCode: null,
      cause: null
    };
    logTenantError('createEdge', errorInfo);
    return fail(errorInfo);
  }

  try {
    const createFn = callTenantFunction('createNewEdge');
    const result = await createFn({id, name, address, geolocation, balenaUuid});
    return ok(result.data);
  } catch (error) {
    const firebaseCode = getFirebaseCode(error);
    const fallbackCode = firebaseCode === 'already-exists' ? 'CREATE_EDGE_ALREADY_EXISTS' : 'CREATE_EDGE_FAILED';
    const errorInfo = toTenantError(error, fallbackCode, 'Failed to create edge');
    logTenantError('createEdge', errorInfo);
    return fail(errorInfo);
  }
};

const regenerateSccCloudKey = async (edgeId) => {
  if (!edgeId) {
    const errorInfo = {
      code: 'INVALID_EDGE_INPUT',
      message: DOMAIN_ERROR_MESSAGES.INVALID_EDGE_INPUT,
      rawCode: null,
      firebaseCode: null,
      cause: null
    };
    logTenantError('regenerateSccCloudKey', errorInfo);
    return fail(errorInfo);
  }

  try {
    const regenerateFn = callTenantFunction('regenerateSccCloudKey');
    const result = await regenerateFn({edgeId});
    return ok(result.data);
  } catch (error) {
    const errorInfo = toTenantError(error, 'CREATE_EDGE_FAILED', 'Failed to regenerate access key');
    logTenantError('regenerateSccCloudKey', errorInfo);
    return fail(errorInfo);
  }
};

const completeEdgeMigration = async (edgeId) => {
  if (!edgeId) {
    const errorInfo = {
      code: 'INVALID_EDGE_INPUT',
      message: DOMAIN_ERROR_MESSAGES.INVALID_EDGE_INPUT,
      rawCode: null,
      firebaseCode: null,
      cause: null
    };
    logTenantError('completeEdgeMigration', errorInfo);
    return fail(errorInfo);
  }

  try {
    const completeFn = callTenantFunction('completeEdgeMigration');
    const result = await completeFn({edgeId});
    return ok(result.data);
  } catch (error) {
    const errorInfo = toTenantError(error, 'COMPLETE_EDGE_MIGRATION_FAILED', 'Failed to complete edge migration');
    logTenantError('completeEdgeMigration', errorInfo);
    return fail(errorInfo);
  }
};

const transferEdge = async ({sourcePath, targetTenantId, edgeId}) => {
  if (!targetTenantId || !edgeId) {
    const errorInfo = {
      code: 'INVALID_TRANSFER_INPUT',
      message: DOMAIN_ERROR_MESSAGES.INVALID_TRANSFER_INPUT,
      rawCode: null,
      firebaseCode: null,
      cause: null
    };
    logTenantError('transferEdge', errorInfo);
    return fail(errorInfo);
  }

  const targetPath = sourcePath.endsWith('/')
    ? `${sourcePath}tenants/${targetTenantId}`
    : `${sourcePath}/tenants/${targetTenantId}`;

  try {
    const transferFn = callTenantFunction('transferEdge');
    await transferFn({sourcePath, targetPath, edgeId});
    return ok();
  } catch (error) {
    const errorInfo = toTenantError(error, 'TRANSFER_EDGE_FAILED', 'Failed to transfer edge');
    logTenantError('transferEdge', errorInfo);
    return fail(errorInfo);
  }
};

const getUserTenantContext = async (user) => {
  if (!user?.email) {
    return null;
  }

  try {
    const userDoc = await getDoc(doc(db, 'users', user.email));
    const userData = userDoc.exists() ? userDoc.data() : null;

    if (!userData?.tenantId || !userData?.tenantPath) {
      return null;
    }

    const tenantDoc = await getDoc(doc(db, userData.tenantPath));
    if (!tenantDoc.exists()) {
      return null;
    }

    return {id: tenantDoc.id, path: userData.tenantPath, role: userData.role, ...tenantDoc.data()};
  } catch (error) {
    const errorInfo = toTenantError(error, 'LOAD_TENANT_CONTEXT_FAILED', 'Failed to load tenant context');
    logTenantError('getUserTenantContext', errorInfo);
    return null;
  }
};

const setCurrentTenantClaim = async (tenantPath) => {
  try {
    const setCurrentTenantFn = callTenantFunction('setCurrentTenant');
    await setCurrentTenantFn({tenantId: tenantPath});

    const auth = getAuth();
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
    }
    return ok();
  } catch (error) {
    const errorInfo = toTenantError(error, 'SET_TENANT_CLAIM_FAILED', 'Failed to set current tenant claims');
    logTenantError('setCurrentTenantClaim', errorInfo);
    return fail(errorInfo);
  }
};

export const getTenantErrorMessage = (errorInfo, fallbackMessage = 'Something went wrong while processing your request.') => {
  if (!errorInfo) return fallbackMessage;
  if (typeof errorInfo === 'string') return errorInfo;
  if (typeof errorInfo?.message === 'string') return errorInfo.message;
  if (typeof errorInfo?.error?.message === 'string') return errorInfo.error.message;
  return fallbackMessage;
};

export const tenantsApi = {
  subscribeTenant,
  subscribeSubTenants,
  createSubTenant,
  addTenantMember,
  removeTenantMember,
  createEdge,
  regenerateSccCloudKey,
  completeEdgeMigration,
  transferEdge,
  getUserTenantContext,
  setCurrentTenantClaim
};
